'use client';

import { useState } from 'react';
import Link from 'next/link';
import { formatDateShort, toInputDateFormat } from '@/lib/utils/formatters';

export interface PaymentRow {
  _id: string;
  amount: number;
  paymentDate: string;
  notes?: string;
  memberId?: number;
  memberName?: string;
  memberLegitId?: string;
}

interface EditForm {
  amount: string;
  paymentDate: string;
  notes: string;
}

interface PaymentsTableProps<T extends PaymentRow> {
  payments: T[];
  showMemberInfo?: boolean;
  onSaveEdit: (paymentId: string, data: { amount: number; paymentDate: string; notes: string }) => Promise<boolean>;
  onDelete?: (payment: T) => void;
}

export function PaymentsTable<T extends PaymentRow>({ payments, showMemberInfo = false, onSaveEdit, onDelete }: PaymentsTableProps<T>) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ amount: '', paymentDate: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const startEditing = (payment: PaymentRow) => {
    setEditingId(payment._id);
    setEditForm({
      amount: payment.amount.toString(),
      paymentDate: toInputDateFormat(payment.paymentDate),
      notes: payment.notes || '',
    });
  };

  const cancelEditing = () => setEditingId(null);

  const saveEditing = async () => {
    if (!editingId) return;
    setSaving(true);
    const success = await onSaveEdit(editingId, {
      amount: parseFloat(editForm.amount),
      paymentDate: editForm.paymentDate,
      notes: editForm.notes,
    });
    if (success) setEditingId(null);
    setSaving(false);
  };

  const inputClass = "bg-zinc-800 border border-zinc-600 rounded px-2 py-1 text-sm text-white";

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-zinc-700 bg-zinc-900/50">
            <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Data</th>
            {showMemberInfo && (
              <>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Nr legit.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Członek</th>
              </>
            )}
            <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Kwota</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Notatki</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Akcje</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-700">
          {payments.map((payment) => {
            const isEditing = editingId === payment._id;

            return (
              <tr key={payment._id} className="hover:bg-zinc-800/50">
                {isEditing ? (
                  <>
                    <td className="px-4 py-2">
                      <input type="date" value={editForm.paymentDate}
                        onChange={(e) => setEditForm({ ...editForm, paymentDate: e.target.value })}
                        className={inputClass} />
                    </td>
                    {showMemberInfo && (
                      <>
                        <td className="px-4 py-2 text-sm font-mono text-zinc-400">{payment.memberLegitId || '-'}</td>
                        <td className="px-4 py-2 text-sm text-zinc-200">
                          {payment.memberId ? (
                            <Link href={`/admin/members/profile?id=${payment.memberId}`} className="hover:text-amber-400 transition-colors">{payment.memberName}</Link>
                          ) : payment.memberName}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2">
                      <input type="number" step="0.01" value={editForm.amount}
                        onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                        className={`${inputClass} w-24 text-right`} />
                    </td>
                    <td className="px-4 py-2">
                      <input type="text" value={editForm.notes} placeholder="Notatka..."
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        className={`${inputClass} w-full`} />
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={saveEditing} disabled={saving}
                        className="text-green-400 hover:text-green-300 p-1 disabled:opacity-50" title="Zapisz">
                        {saving ? '...' : '✓'}
                      </button>
                      <button onClick={cancelEditing} disabled={saving}
                        className="text-red-400 hover:text-red-300 p-1 ml-1 disabled:opacity-50" title="Anuluj">
                        ✕
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 text-sm text-zinc-300">{formatDateShort(payment.paymentDate)}</td>
                    {showMemberInfo && (
                      <>
                        <td className="px-4 py-3 text-sm font-mono text-zinc-400">{payment.memberLegitId || '-'}</td>
                        <td className="px-4 py-3 text-sm text-zinc-200">
                          {payment.memberId ? (
                            <Link href={`/admin/members/profile?id=${payment.memberId}`} className="hover:text-amber-400 transition-colors">{payment.memberName}</Link>
                          ) : payment.memberName}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-sm text-right text-green-400 font-medium">{payment.amount.toFixed(2)} zł</td>
                    <td className="px-4 py-3 text-sm text-zinc-400">{payment.notes || '—'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => startEditing(payment)}
                        className="text-zinc-400 hover:text-white p-1" title="Edytuj wpłatę">✏️</button>
                      {onDelete && (
                        <button onClick={() => onDelete(payment)}
                          className="text-red-400 hover:text-red-300 p-1 ml-1" title="Usuń wpłatę">🗑️</button>
                      )}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

